/**
 * Where the first-run welcome screen lives, and the one piece of state that is not
 * the member's own record.
 *
 * `onboarding_completed` on the account is the real answer, but writing it can fail —
 * a phone on a dead train line, a 502. Trapping a member on the welcome screen because
 * the acknowledgement did not reach the server would be a far worse outcome than
 * showing the page once more on the next app start, so pressing the call to action
 * also raises this session-scoped flag. The gate honours either.
 *
 * The flag is held *per account id*, not as a bare boolean. Sign-out does not reload
 * the page — it only resets the store — so a process-wide flag would follow the device
 * into the next member's session and swallow their welcome screen entirely. Deliberately
 * module state rather than storage: it must not survive the process either, or a failed
 * write would be lost for good.
 */

export const WELCOME_PATH = "/welcome";

let passedFor: string | null = null;

/** Called by the welcome screen's call to action, whatever the API answered. */
export function markWelcomePassed(userId: string): void {
  passedFor = userId;
}

/** Whether this member has already pressed through the welcome screen since boot. */
export function welcomePassed(userId: string): boolean {
  return passedFor === userId;
}

/** Test seam — the flag is module state, so a spec has to be able to clear it. */
export function resetWelcomePassed(): void {
  passedFor = null;
}

/** Whether this member still has to be routed through the welcome screen. */
export function needsWelcome(
  user: { id: string; onboarding_completed: boolean } | null,
): boolean {
  return user !== null && !user.onboarding_completed && !welcomePassed(user.id);
}
